import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { LockRewardRepository, LockRewardsOnChainTransport } from "@/domains/lock-rewards"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVenammStore } from "@/domains/venamm"
import type { IVoteOnChainTransport } from "@/domains/vote/interfaces"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { OrchestratedQuery } from "@/domains/library"

import type { LockApiData } from "./types"
import { LockApiTransport } from "./lock-api-transport"
import { LockOrchestrator } from "./lock-orchestrator"
import { LockRepository } from "./lock-repository"
import { WalletAddressSource } from "./wallet-address-source"
import { WalletEventStrategy } from "./wallet-event-strategy"

export class LockOrchestratorFactory {
  /**
   * Create a fully configured LockOrchestrator with its OrchestratedQuery
   */
  static create(
    lockApiTransport: LockApiTransport,
    lockRepository: LockRepository,
    walletStore: IWalletConnectionStore,
    orchestratedQueryStore: OrchestratedQueryStore,
    lockRewardsOnChainTransport: LockRewardsOnChainTransport,
    lockRewardRepository: LockRewardRepository,
    tokenLookup: ITokenLookup,
    lockOnChainTransport: LockOnChainTransport,
    eventBus: IEventBus,
    voteOnChainTransport: IVoteOnChainTransport,
    queryNamePrefix?: string,
  ): LockOrchestrator {
    // 1. Create address source for wallet-based address
    const addressSource = new WalletAddressSource(walletStore)

    // 2. Create orchestrator with all dependencies
    const orchestrator = new LockOrchestrator(
      lockRepository,
      lockApiTransport,
      lockOnChainTransport,
      walletStore,
      lockRewardsOnChainTransport,
      lockRewardRepository,
      tokenLookup,
      orchestratedQueryStore,
      eventBus,
      voteOnChainTransport,
      addressSource,
      queryNamePrefix,
      new WalletEventStrategy(), // Normal orchestrator uses default strategy
    )

    // 3. Create locks sync query with 60-second interval
    const loadLocksQuery = new OrchestratedQuery<Array<LockApiData>>(
      "Lock NFTs Sync",
      (query) => orchestrator.onApiLoadLocksTrigger(query),
      60_000,
      orchestrator.onApiLoadLocksQuerySuccess.bind(orchestrator),
      orchestrator.onApiLoadLocksQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 4. Create lock info sync query with 30-second interval
    const loadLockInfoQuery = new OrchestratedQuery<bigint[]>(
      "Lock Info On-Chain Sync",
      (query) => orchestrator.onChainLoadLockInfoTrigger(query),
      30_000,
      orchestrator.onChainLoadLockInfoQuerySuccess.bind(orchestrator),
      orchestrator.onChainLoadLockInfoQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 5. Create voting info sync query with 60-second interval
    const loadVotingInfoQuery = new OrchestratedQuery<any>(
      "Voting Info On-Chain Sync",
      (query) => orchestrator.onChainLoadVotingInfoTrigger(query),
      60_000,
      orchestrator.onLoadVotingInfoQuerySuccess.bind(orchestrator),
      orchestrator.onLoadVotingInfoQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // 6. Initialize orchestrator with all queries
    orchestrator.initialize(loadLocksQuery, loadLockInfoQuery, loadVotingInfoQuery)

    // 7. Note: We don't auto-start here - the orchestrator starts on wallet connect

    return orchestrator
  }
}
